export * from "./ip";

export type OpsanioSizing = "small" | "medium" | "large";
export type OpsanioServiceModel = "starter" | "basic" | "medium" | "enterprise";
export const OPSANIO_SIZES: OpsanioSizing[] = ["small", "medium", "large"];
export const OPSANIO_SERVICE_MODELS: OpsanioServiceModel[] = [
  "starter",
  "basic",
  "medium",
  "enterprise",
];
export interface AutonubilHomeApplianceFeaturesBadge {
  retention: number; // Count in days
  agentCount: number | string;
  storage: { size: number; unit: "TB" | "GB" };
  ram: { size: number; unit: "TB" | "GB" };
  cpu?: {
    count: number;
    model: string;
  };
}

export interface HardwareConfig {
  /**
   * The appliance's compute nodes. Named for what they are, not for a vendor -
   * the estate runs HPE ProLiants, and calling the line "supermicro" made the
   * cost calculator label every quotation with a manufacturer it does not buy.
   */
  server: {
    model: string;
    cost: number;
    nodeCount: number;
  };
  switch: {
    model: string;
    cost: number;
    count: number;
  };
  cable: {
    patch: {
      cost: number;
      count: number;
    };
    transceiver: {
      cost: number;
      count: number;
    };
  };
}

export interface OpsanioModels extends HardwareConfig {
  specs: AutonubilHomeApplianceFeaturesBadge;
  cost: {
    monthly: number;
  };
}

export const OPSANIO_SERVICE_MODELS_ARRAY: Map<
  OpsanioServiceModel,
  OpsanioModels
> = new Map([
  [
    "starter",
    {
      server: {
        model: "",
        cost: 0,
        nodeCount: 0,
      },
      switch: {
        cost: 0,
        model: "",
        count: 0,
      },
      cable: {
        patch: {
          count: 0,
          cost: 0,
        },
        transceiver: {
          count: 0,
          cost: 0,
        },
      },
      specs: {
        agentCount: 50,
        ram: {
          size: 265,
          unit: "GB",
        },
        retention: 30,
        storage: {
          size: 500,
          unit: "GB",
        },
      },
      cost: {
        monthly: 1500,
      },
    },
  ],
  [
    "basic",
    {
      server: {
        model: "",
        cost: 0,
        nodeCount: 0,
      },
      switch: {
        cost: 0,
        model: "",
        count: 0,
      },
      cable: {
        patch: {
          count: 0,
          cost: 0,
        },
        transceiver: {
          count: 0,
          cost: 0,
        },
      },
      specs: {
        agentCount: 100,
        ram: {
          size: 265,
          unit: "GB",
        },
        retention: 30,
        storage: {
          size: 500,
          unit: "GB",
        },
      },
      cost: {
        monthly: 2500,
      },
    },
  ],
  [
    "medium",
    {
      server: {
        model: "AS-2115GT-HNTR",
        cost: 17629.0,
        nodeCount: 4,
      },
      switch: {
        cost: 629.02,
        model: "HPE InstantOn 1960 12XGT 4SFP+ Switch",
        count: 1,
      },
      cable: {
        patch: {
          count: 17,
          cost: 1.3,
        },
        transceiver: {
          count: 1,
          cost: 23.8,
        },
      },
      specs: {
        agentCount: "100+",
        ram: {
          size: 1,
          unit: "TB",
        },
        retention: 90,
        storage: {
          size: 11.5,
          unit: "TB",
        },
        cpu: {
          count: 4,
          model: "AMD EPYC Turin 9135",
        },
      },
      cost: {
        monthly: 4000,
      },
    },
  ],
  [
    "enterprise",
    {
      server: {
        has: true,
        model: "AS-3015MR-H8TNR",
        cost: 23834.0,
        nodeCount: 8,
      },
      switch: {
        cost: 629.02,
        model: "HPE InstantOn 1960 12XGT 4SFP+ Switch",
        count: 2,
      },
      cable: {
        patch: {
          count: 34,
          cost: 1.3,
        },
        transceiver: {
          count: 2,
          cost: 23.8,
        },
      },
      specs: {
        agentCount: "250+",
        ram: {
          size: 1.5,
          unit: "TB",
        },
        retention: 180,
        storage: {
          size: 23,
          unit: "TB",
        },
        cpu: {
          count: 8,
          model: "AMD EPYC 4565P",
        },
      },
      cost: {
        monthly: 9000,
      },
    },
  ],
]);

export interface OpsanioSizeValues {
  opensearch_search_instances: number;
  opensearch_master_instances: number;
  opensearch_master_cpu: number;
  opensearch_master_ram: number;
  cpu_socket_count: number;
  opensearch_client_cpu: number;
  opensearch_client_ram: number;
  opensearch_client_instances: number;
  opensearch_search_cpu: number;
  opensearch_search_ram: number;
  opensearch_hot_per_node: number;
  opensearch_hot_cpu: number;
  opensearch_hot_ram: number;
  opensearch_warm_per_node: number;
  opensearch_warm_cpu: number;
  opensearch_warm_ram: number;
  opensearch_cold_per_node: number;
  opensearch_cold_cpu: number;
  opensearch_cold_ram: number;
}

/**
 * @deprecated Dead copy - nothing imports this. The map the generated values
 * file actually comes from is in helper/parser/opsanio.ts, and its numbers have
 * diverged from these. Edit that one; editing this changes nothing. Kept for one
 * release rather than deleted because it is exported.
 */
export const opsanioSizingMap: Map<OpsanioSizing, OpsanioSizeValues> = new Map([
  [
    "small",
    {
      opensearch_master_instances: 3,
      opensearch_master_cpu: 3000,
      opensearch_master_ram: 8192,
      cpu_socket_count: 4,
      opensearch_client_cpu: 4000,
      opensearch_client_ram: 8192,
      opensearch_client_instances: 2,
      opensearch_search_cpu: 4000,
      opensearch_search_ram: 8192,
      opensearch_search_instances: 2,
      opensearch_hot_per_node: 1,
      opensearch_hot_cpu: 2000,
      opensearch_hot_ram: 12288,
      opensearch_warm_per_node: 1,
      opensearch_warm_cpu: 800,
      opensearch_warm_ram: 3192,
      opensearch_cold_per_node: 1,
      opensearch_cold_cpu: 800,
      opensearch_cold_ram: 3192,
    },
  ],
  [
    "medium",
    {
      opensearch_master_instances: 3,
      opensearch_master_cpu: 3000,
      opensearch_master_ram: 8192,
      cpu_socket_count: 6,
      opensearch_client_cpu: 6000,
      opensearch_client_ram: 12288,
      opensearch_client_instances: 2,
      opensearch_search_cpu: 4000,
      opensearch_search_ram: 12288,
      opensearch_search_instances: 2,
      opensearch_hot_per_node: 2,
      opensearch_hot_cpu: 3000,
      opensearch_hot_ram: 32768,
      opensearch_warm_per_node: 2,
      opensearch_warm_cpu: 1000,
      opensearch_warm_ram: 6144,
      opensearch_cold_per_node: 1,
      opensearch_cold_cpu: 1000,
      opensearch_cold_ram: 4096,
    },
  ],
  [
    "large",
    {
      opensearch_master_instances: 3,
      opensearch_master_cpu: 3000,
      opensearch_master_ram: 8192,
      cpu_socket_count: 8,
      opensearch_client_cpu: 8000,
      opensearch_client_ram: 12288,
      opensearch_client_instances: 4,
      opensearch_search_cpu: 4000,
      opensearch_search_ram: 16384,
      opensearch_search_instances: 4,
      opensearch_hot_per_node: 8,
      opensearch_hot_cpu: 4000,
      opensearch_hot_ram: 32768,
      opensearch_warm_per_node: 4,
      opensearch_warm_cpu: 1500,
      opensearch_warm_ram: 12288,
      opensearch_cold_per_node: 2,
      opensearch_cold_cpu: 1500,
      opensearch_cold_ram: 6144,
    },
  ],
]);
